import { Component, h, Prop, Event, EventEmitter, State, Watch, Fragment } from "@stencil/core";
import { type FormEvent, CATEGORIZED_OCCUPATION_OPTIONS, CATEGORIZED_OCCUPATION_OPTIONS_CAMPUS, CountryOptions, PianoTerm, YEARS_OF_EXPERIENCE_OPTIONS } from "../types";
import AuthHeader from "../auth-header";
import { updateFieldValidations } from "../utils";
import { countriesList } from "../../../utils/localized_countries";

const INDUSTRIES_WITHOUT_OCCUPATION = ["Architecture Enthusiast", "Other"];
@Component({
  tag: 'hd-registration',
  shadow: false
})
export class HdRegistration {
  @Prop({ reflect: true }) type: "signup" | "login" | "external_profile" | "recover" | "check-inbox" = "signup";
  @Prop() occupationOptions: string[] = [];
  @Prop({ mutable: true, reflect: true }) email: string = '';
  @Prop() campusVersion: boolean = false;
  @Prop({ mutable: true }) eventSource: string = '';
  @Prop() paymentDetails?: { experienceId: string, offerId: string, termId: string, term: PianoTerm } = null;
  @Prop() experienceId?: string;
  @State() full_name: string = '';
  @State() country: string = '';
  @State() industry: string = '';
  @State() occupation: string = '';
  @State() full_occupation: string = '';
  @State() experience: string = '';
  @State() password: string = '';
  @Event() nextStep: EventEmitter<FormEvent>;
  @Event() prevStep: EventEmitter<FormEvent>;
  @Event() registrationExited: EventEmitter<{ closeReason: string, currentStep: string }>;
  @State() externalProvider: string;
  @State() errorFormFields: Record<string, boolean | null> = {
    full_name: null,
    email: null,
    password: null,
    country: null,
    industry: null,
    occupation: null,
    experience: null,
  }
  @State() occupationPerIndustryOptions: Record<string, string[]> = this.campusVersion ? CATEGORIZED_OCCUPATION_OPTIONS_CAMPUS : CATEGORIZED_OCCUPATION_OPTIONS;
  @State() loading = false;
  @State() isFormValid = false;
  recaptchaId: string = '';
  countryOptions: CountryOptions = countriesList as unknown as CountryOptions;

  private validationTimeouts: Record<string, number> = {};

  componentWillLoad() {
    const searchParams = new URLSearchParams(window.location.search);
    this.externalProvider = searchParams.get("provider");

    this.country = window.AFD.countryCode ?? "";

    this.errorFormFields = {
      full_name: null,
      email: this.externalProvider === "facebook" ? null : true,
      password: this.externalProvider !== null ? true : null, // Password is not required for external providers
      country: this.country.length > 0 ? true : null,
      industry: null,
      occupation: null,
      experience: null,
    }
  }

  componentDidLoad() {
    let recaptcha = document.getElementById("hd-auth-recaptcha");
    if (!recaptcha) {
      const hdAuth = document.querySelector("hd-auth");
      hdAuth.insertAdjacentHTML("afterend", `<div id="hd-auth-recaptcha"></div>`);
      recaptcha = document.getElementById("hd-auth-recaptcha");
    }
    if (window.grecaptcha) {
      this.recaptchaId = window.grecaptcha.render(recaptcha, {
        "sitekey": "6LdAAXgUAAAAAF4SGaUBUb360KEclFkQDCm4Lyj2",
        "badge": "bottomright",
        "type": "image",
        "size": "invisible",
        "callback": this.createUser
      });
      (recaptcha.children[0] as HTMLElement).style.zIndex = "100000";
    } else {
      console.error("grecaptcha not found");
    }
  }

  disconnectedCallback() {
    window.grecaptcha.reset(this.recaptchaId);
    let recaptcha = document.getElementById("hd-auth-recaptcha");
    if (recaptcha) {
      recaptcha.remove();
    }
  }

  @Watch("industry")
  industryChanged(newIndustry: string) {
    this.occupation = "";
    this.experience = "";
    this.errorFormFields.occupation = null;
    this.errorFormFields.experience = null;

    if (INDUSTRIES_WITHOUT_OCCUPATION.includes(newIndustry)) {
      this.errorFormFields.occupation = true;
      this.errorFormFields.experience = true;
    }
  }

  @Watch("occupation")
  occupationChanged(newOccupation: string) {
    if (newOccupation === "Student") {
      this.errorFormFields.experience = true;
    } else {
      this.errorFormFields.experience = this.experience.length > 0 ? true : null;
    }
  }

  @Watch("campusVersion")
  campusVersionChanged(newCampusVersion: boolean) {
    this.occupationPerIndustryOptions = newCampusVersion ? CATEGORIZED_OCCUPATION_OPTIONS_CAMPUS : CATEGORIZED_OCCUPATION_OPTIONS;
  }

  createUser = (token: string) => {
    this.nextStep.emit({
      full_name: this.full_name,
      email: this.email,
      occupation: this.full_occupation,
      years_of_experience: this.experience,
      country_code: this.country,
      password: this.password,
      token,
    });
  }

  completeExternalProfile = () => {
    this.nextStep.emit({
      full_name: this.full_name,
      email: this.email,
      occupation: this.full_occupation,
      years_of_experience: this.experience,
      country_code: this.country,
    });
  }

  yearsOfExperienceEnabled = () => {
    return this.occupation.length > 0 && this.occupation !== "Student";
  }

  submit(e: Event) {
    e.preventDefault();
    this.loading = true;
    if (this.type === "external_profile") {
      this.completeExternalProfile();
    } else {
      window.grecaptcha.execute(this.recaptchaId);
    }
  }

  updateField(field: string, e: Event) {
    const target = e.target as HTMLInputElement;

    this[field] = (e.target as HTMLInputElement).value;

    if (field === 'industry') {
      this.occupation = '';

      if (!this.occupationPerIndustryOptions[this.industry].length) {
        this.full_occupation = this.industry;
      }
    }

    if (field === 'occupation') {
      this.full_occupation = `${this.industry} | ${this.occupation}`;
    }

    if (this.validationTimeouts[field]) {
      clearTimeout(this.validationTimeouts[field]);
    }

    this.validationTimeouts[field] = window.setTimeout(() => {
      this.validateField(field, target);
    }, 500);
  }

  validateField = (field: string, target: HTMLInputElement) => {
    const isValid = updateFieldValidations(field, target);
    this.errorFormFields[field] = isValid;
    this.isFormValid = Object.values(this.errorFormFields).every(isValid => isValid === true);
  }

  render() {
    return (
      <form class="form-container step-container" onSubmit={this.submit.bind(this)}>
        <AuthHeader
          title={this.campusVersion ? "Complete Your Campus Profile" : "Complete Your Profile"}
          subhead="Tell us a bit about yourself to get the most out of ArchDaily."
        />
        <div class="form-body">
          {(this.type !== "external_profile" || this.externalProvider === "facebook") && (
            <div class="form-group">
              <label htmlFor="email">Email</label>
              <input type="text" id="email" value={this.email}
                disabled={this.campusVersion}
                onInput={(e) => this.updateField('email', e)}
                onBlur={(e) => this.validateField('email', e.target as HTMLInputElement)}
              />
              <p class="error-message"></p>
            </div>
          )}

          <div class="form-row">
            <div class="form-group">
              <label htmlFor="full_name">Full Name</label>
              <input type="text" id="full_name" value={this.full_name}
                onInput={(e) => this.updateField('full_name', e)}
                onBlur={(e) => this.validateField('full_name', e.target as HTMLInputElement)}
              />
              <p class="error-message"></p>
            </div>

            {this.type !== "external_profile" && (
              <div class="form-group">
                <label htmlFor="password">Password</label>
                <input type="password" id="password"
                  value={this.password}
                  onInput={(e) => this.updateField('password', e)}
                  onBlur={(e) => this.validateField('password', e.target as HTMLInputElement)}
                />
                <p class="error-message"></p>
              </div>
            )}
          </div>

          <div class="form-group">
            <label htmlFor="country">Country / Region</label>
            <select id="country"
              onChange={(e) => this.updateField('country', e)}
            >
              <option value="" disabled selected={this.country === ''}>Select country</option>
              {Object.entries(this.countryOptions["en"]).map(([code, name]) => (
                <option value={code} selected={code === this.country}>{name}</option>
              ))}
            </select>
            <p class="error-message"></p>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label htmlFor="industry">Industry</label>
              <select id="industry"
                onChange={(e) => this.updateField('industry', e)}
              >
                <option value="" disabled selected>Select industry</option>
                {Object.keys(this.occupationPerIndustryOptions).map(option => (
                  <option value={option}>{option}</option>
                ))}
              </select>
              <p class="error-message"></p>
            </div>
            <div class="form-group">
              {!INDUSTRIES_WITHOUT_OCCUPATION.includes(this.industry) && (
                <Fragment>
                  <label htmlFor="occupation">Occupation</label>
                  <select id="occupation"
                    disabled={!this.industry || !this.occupationPerIndustryOptions[this.industry].length}
                    onChange={(e) => this.updateField('occupation', e)}
                  >
                    <option value="" disabled selected={this.occupation === ''}>Select occupation</option>
                    {(this.occupationPerIndustryOptions[this.industry] || []).map(option => (
                      <option value={option}>{option}</option>
                    ))}
                  </select>
                  <p class="error-message"></p>
                </Fragment>
              )}
            </div>
          </div>

          {
            this.yearsOfExperienceEnabled() && (
              <div class="form-group">
                <label htmlFor="experience">Years of Experience</label>
                <select id="experience"
                  onChange={(e) => this.updateField('experience', e)}
                >
                  <option value="" disabled selected>Select years of experience</option>
                  {YEARS_OF_EXPERIENCE_OPTIONS.map(option => (
                    <option value={option}>{option}</option>
                  ))}
                </select>
                <p class="error-message"></p>
              </div>
            )
          }
          <div class="step-actions">
            {this.type !== "external_profile" && !this.campusVersion && (
              <button class="prev-step" onClick={() => this.prevStep.emit()}>
                Previous
              </button>
            )}
            <hd-button primary padded class="next-step" onClick={this.submit.bind(this)}
              disabled={!this.isFormValid || this.loading}>
              {this.loading ? <hd-icon svg="loader_bouncing_circles" width="24" height="24" /> : 'Next'}
            </hd-button>
          </div>
        </div>
      </form>
    )
  }
}
